import { DeleteForever, MinorCrash } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { addVehicles, removeVehicles } from 'controllers/outboundTrips/index'
import { OutboundTripDetailDto } from 'dtos/outboundTrips'
import { OutboundTripDetailVehicleDto } from 'dtos/outboundTrips/OutboundTripDetailVehicleDto'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { clientSideOrderBy, formatDateMoment } from 'utils'
import AddVehiclesDialog from './AddVehiclesDialog'

interface ManageVehiclesCardProps {
  outboundTripGuid?: string
  setState: (outboundTripDetail: OutboundTripDetailDto) => void
  vehicles?: OutboundTripDetailVehicleDto[]
}

export default function ManageVehiclesCard({ outboundTripGuid, setState, vehicles }: ManageVehiclesCardProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isRemoving, setIsRemoving] = useState<boolean>(false)

  const [selectedVehicles, setSelectedVehicles] = useState<OutboundTripDetailVehicleDto[]>([])
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OutboundTripDetailVehicleDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const onChangeSortByDirection = (propertyName: keyof OutboundTripDetailVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <>
      {outboundTripGuid && (
        <AddVehiclesDialog
          isLoading={isSaving}
          onClose={() => setDialogOpen(false)}
          onSave={values => {
            setIsSaving(true)
            addVehicles(outboundTripGuid, values)
              .then(res => {
                enqueueSnackbar('Vehicles Added Successfully!', { variant: 'success' })
                setState({ vehicles: res.value })
                setDialogOpen(false)
              })
              .finally(() => setIsSaving(false))
          }}
          open={dialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h6'>
                  Manage Vehicles
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1} alignItems='flex-end'>
                <Grid item xs={12} sm='auto'>
                  <LoadingButton
                    color='error'
                    disabled={selectedVehicles.length === 0}
                    endIcon={<DeleteForever />}
                    loading={isRemoving}
                    onClick={() => {
                      setIsRemoving(true)
                      removeVehicles(outboundTripGuid!, selectedVehicles)
                        .then(res => {
                          enqueueSnackbar('Vehicles Removed Successfully!', { variant: 'success' })
                          setState({ vehicles: res.value })
                          setSelectedVehicles([])
                          setPage(0)
                        })
                        .finally(() => setIsRemoving(false))
                    }}
                    variant='text'
                  >
                    REMOVE SELECTED
                  </LoadingButton>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button color='primary' disabled={isRemoving} endIcon={<MinorCrash />} onClick={() => setDialogOpen(true)} variant='text'>
                    ADD VEHICLES
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={selectedVehicles.length === vehicles?.length && selectedVehicles.length !== 0}
                        disabled={!vehicles?.length || isRemoving}
                        indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== vehicles?.length}
                        onChange={() => {
                          if (selectedVehicles.length === vehicles?.length) {
                            setSelectedVehicles([])
                          } else if (vehicles) {
                            setSelectedVehicles(vehicles)
                          }
                        }}
                      />
                    </TableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='importerStockNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Last 6 Of VIN
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Model
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='exporterBuyerCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Exporter/Buyer
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='submittedToDOTDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Submitted To DOT
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='readyForOutboundDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Ready For Outbound Date
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='vehicleReleaseDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Vehicle Release Date
                    </OrderableTableCell>

                    <OrderableTableCell<OutboundTripDetailVehicleDto>
                      keyName='bondReleaseDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Bond Release Date
                    </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vehicles?.sort(clientSideOrderBy<OutboundTripDetailVehicleDto>(orderBy, order)).map((vehicle, index) => (
                    <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          color='primary'
                          checked={selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)}
                          disabled={isRemoving}
                          onChange={() => {
                            if (selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)) {
                              setSelectedVehicles(selectedVehicles.filter(selectedVehicle => selectedVehicle.vehicleGuid !== vehicle.vehicleGuid))
                            } else {
                              setSelectedVehicles([...selectedVehicles, vehicle])
                            }
                          }}
                        />
                      </TableCell>

                      <TableCell>{vehicle.importerStockNumber}</TableCell>

                      <TableCell>{vehicle.vin.substring(vehicle.vin.length - 6)}</TableCell>

                      <TableCell>{vehicle.year}</TableCell>

                      <TableCell>{vehicle.make}</TableCell>

                      <TableCell>{vehicle.model}</TableCell>

                      <TableCell>{vehicle.exporterBuyerCompany}</TableCell>

                      <TableCell>{vehicle.submittedToDOTDateTime ? formatDateMoment(vehicle.submittedToDOTDateTime) : '-'}</TableCell>

                      <TableCell>{vehicle.readyForOutboundDateTime ? formatDateMoment(vehicle.readyForOutboundDateTime) : '-'}</TableCell>

                      <TableCell>{vehicle.vehicleReleaseDateTime ? formatDateMoment(vehicle.vehicleReleaseDateTime) : '-'}</TableCell>

                      <TableCell>{vehicle.bondReleaseDateTime ? formatDateMoment(vehicle.bondReleaseDateTime) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={vehicles?.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Remove Selected Button:**

Clicking this button removes all selected vehicles from the Manage Vehicles Card.

This button is enabled only when at least one vehicle is selected.

**Add Vehicles Button:**

Clicking this button opens the Add Vehicles dialog.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
